import { apiClient } from './client';

export interface Integration {
  id: string;
  provider: string;
  name: string;
  status: 'active' | 'inactive' | 'error' | 'pending';
  config: Record<string, unknown>;
  lastSyncAt: string | null;
  lastSyncStatus: string | null;
  lastSyncError: string | null;
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string;
  syncRuns?: SyncRun[];
}

export interface SyncRun {
  id: string;
  integrationId: string;
  provider: string;
  status: 'idle' | 'running' | 'completed' | 'failed';
  entitiesSynced: number;
  entitiesFailed: number;
  startedAt: string;
  completedAt: string | null;
  error: string | null;
}

export interface ConnectionTestResult {
  success: boolean;
  message: string;
}

export async function getIntegrations(): Promise<{ integrations: Integration[] }> {
  return apiClient.get('/integrations');
}

export async function getIntegration(id: string): Promise<{ integration: Integration }> {
  return apiClient.get(`/integrations/${id}`);
}

export async function createIntegration(data: {
  provider: string;
  name: string;
  config: Record<string, unknown>;
}): Promise<{ integration: Integration }> {
  return apiClient.post('/integrations', data);
}

export async function updateIntegration(
  id: string,
  data: {
    name?: string;
    config?: Record<string, unknown>;
    enabled?: boolean;
  }
): Promise<{ integration: Integration }> {
  return apiClient.patch(`/integrations/${id}`, data);
}

export async function deleteIntegration(id: string): Promise<{ success: boolean }> {
  return apiClient.delete(`/integrations/${id}`);
}

export async function testIntegration(id: string): Promise<{ result: ConnectionTestResult }> {
  return apiClient.post(`/integrations/${id}/test`);
}

export async function triggerSync(
  id: string,
  options?: { entities?: string[]; fullSync?: boolean }
): Promise<{ syncRun: SyncRun }> {
  return apiClient.post(`/integrations/${id}/sync`, options ?? {});
}

export async function getSyncRuns(
  id: string,
  params?: { limit?: number; offset?: number }
): Promise<{ syncRuns: SyncRun[] }> {
  return apiClient.get(`/integrations/${id}/sync-runs`, { params });
}

export async function verifySlackToken(token: string): Promise<{ ok: boolean; appName?: string; team?: string; error?: string }> {
  return apiClient.post('/integrations/slack/verify-token', { token });
}

export async function getGoogleAdsAuthUrl(integrationId?: string): Promise<{ url: string }> {
  return apiClient.get('/integrations/google-ads/auth-url', {
    params: integrationId ? { integrationId } : undefined,
  });
}

export async function exchangeGoogleAdsCode(code: string, state: string, integrationId?: string): Promise<{ integration: Integration }> {
  return apiClient.post('/integrations/google-ads/callback', { code, integrationId, state });
}

export async function getBingAdsAuthUrl(integrationId: string): Promise<{ url: string }> {
  return apiClient.get('/integrations/bing-ads/auth-url', { params: { integrationId } });
}

export async function exchangeBingAdsCode(code: string, state: string, integrationId: string): Promise<{ integration: Integration }> {
  return apiClient.post('/integrations/bing-ads/callback', { code, integrationId, state });
}

export async function getLinkedInOrganicAuthUrl(integrationId: string, person: string): Promise<{ url: string }> {
  return apiClient.get('/integrations/linkedin-organic/auth-url', {
    params: { integrationId, person },
  });
}

export async function exchangeLinkedInOrganicCode(
  code: string,
  state: string,
  integrationId: string,
  person: string,
): Promise<{ integration: Integration; connectedPerson: { name: string; person: string; personUrn: string } }> {
  return apiClient.post('/integrations/linkedin-organic/callback', { code, integrationId, person, state });
}
